import React, { useState, useEffect } from 'react';
import './OnlineQuranClassesInNewyork.css';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';

const OnlineQuranClassesCanada = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });

  // Animated counters state
  const [experience, setExperience] = useState(0);
  const [students, setStudents] = useState(0);
  const [tutors, setTutors] = useState(0);

  // FAQ active state mapping (all closed by default)
  const [activeFaqs, setActiveFaqs] = useState({});

  const toggleFaq = (index) => {
    setActiveFaqs(prev => {
      const isOpen = prev[index];
      return isOpen ? {} : { [index]: true };
    });
  };

  // Testimonials Slider State
  const [currentSlide, setCurrentSlide] = useState(0);
  const testimonials = [
    {
      text: "I was concerned about my child’s religious education, and now I am very pleased to see that they offer Quran classes in Canada. They have very nice and skilled tutors, and the ability to attend classes without disturbing school commitments was a huge bonus.",
      author: "Aisha - Toronto"
    },
    {
      text: "I joined their online Quran with Tajweed classes in Canada. The teachers are professional, and the individual attention and support that I received during the Tajweed classes were excellent. The one-on-one lessons are very helpful to correct minor mistakes quickly.",
      author: "Yusuf - Vancouver"
    },
    {
      text: "My children look forward to their classes and love to learn because of the knowledgeable and supportive Quran education their teachers provide. I highly recommend The Quran Academy Canada for anyone looking for Quran teachers near me in Canada.",
      author: "Mariam - Calgary"
    },
    {
      text: "I was looking for Quran teachers near me in Canada, and then I enrolled there. From the free trial to regular classes, everything has been smooth. The tutors focus on real progress. One of the best platforms for online Quran classes for kids in Canada.",
      author: "Ibrahim - Mississauga"
    }
  ];

  // Auto-play timer for testimonials (5 seconds)
  useEffect(() => {
    const slideTimer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(slideTimer);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Contact Form State
  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    course: '',
    message: '',
    consent: false
  });

  const handleContactChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactData.consent) {
      Swal.fire({
        title: 'Consent Required',
        text: "Please consent to terms to proceed.",
        icon: 'warning',
        confirmButtonColor: '#5c9456'
      });
      return;
    }
    try {
      await submitForm('Canada Contact Form', contactData);
      Swal.fire({
        title: 'Success!',
        text: `Thank you, ${contactData.firstName} ${contactData.lastName}! We have received your message.`,
        icon: 'success',
        confirmButtonColor: '#5c9456'
      });
      setContactData({
        firstName: '',
        lastName: '',
        phone: '',
        country: '',
        course: '',
        message: '',
        consent: false
      });
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to submit form. Please try again.',
        icon: 'error',
        confirmButtonColor: '#5c9456'
      });
    }
  };

  useEffect(() => {
    // Load Google reCAPTCHA v2 API script dynamically if not already loaded
    const scriptId = 'google-recaptcha-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    // Experience counter (0 to 16)
    let expStart = 0;
    const expEnd = 16;
    const expDuration = 1200; // ms
    const expStepTime = Math.max(Math.floor(expDuration / expEnd), 15);
    const expTimer = setInterval(() => {
      expStart += 1;
      setExperience(expStart);
      if (expStart >= expEnd) {
        clearInterval(expTimer);
      }
    }, expStepTime);

    // Tutors counter (0 to 100)
    let tutorsStart = 0;
    const tutorsEnd = 100;
    const tutorsDuration = 1200; // ms
    const tutorsStep = 2;
    const tutorsStepTime = Math.max(Math.floor(tutorsDuration / (tutorsEnd / tutorsStep)), 15);
    const tutorsTimer = setInterval(() => {
      tutorsStart += tutorsStep;
      setTutors(tutorsStart);
      if (tutorsStart >= tutorsEnd) {
        clearInterval(tutorsTimer);
      }
    }, tutorsStepTime);

    // Students counter (0 to 5000)
    let studentsStart = 0;
    const studentsEnd = 5000;
    const studentsDuration = 1200; // ms
    const studentsStep = 100;
    const studentsStepTime = Math.max(Math.floor(studentsDuration / (studentsEnd / studentsStep)), 15);
    const studentsTimer = setInterval(() => {
      studentsStart += studentsStep;
      setStudents(studentsStart);
      if (studentsStart >= studentsEnd) {
        clearInterval(studentsTimer);
      }
    }, studentsStepTime);

    return () => {
      clearInterval(expTimer);
      clearInterval(tutorsTimer);
      clearInterval(studentsTimer);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitForm('Canada Registration Form', formData);
      Swal.fire({
        title: 'Success!',
        text: `Thank you, ${formData.name}! Your request has been sent successfully. We will contact you soon.`,
        icon: 'success',
        confirmButtonColor: '#5c9456'
      });
      setFormData({ name: '', phone: '', message: '' });
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to submit form. Please try again.',
        icon: 'error',
        confirmButtonColor: '#5c9456'
      });
    }
  };

  return (
    <div className="ny-page-wrapper">
      {/* Hero Section */}
      <section
        className="ny-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-canada/Hero-1.avif')`
        }}
      >
        <div className="ny-hero__container">
          {/* Left Column: Heading, description, and stats */}
          <div className="ny-hero__content">
            <h1 className="ny-hero__title">
              Online
              <span className="ny-hero__title--highlight"> Quran Classes In Canada</span>
              <span className="ny-hero__title--main"> for Kids & Adults</span>
            </h1>
            <p className="ny-hero__desc">
              Al Quran Lesson offers online Quran classes in Canada for kids and adults, helping students learn Quran recitation with proper Tajweed since 2013. Our focus is on teaching the correct rules of reciting the Quran with proper Tajweed techniques, thereby helping students in their Quran recitation, spiritual development, and learning process. Start your child’s Quran learning today with a free 3-day trial class to learn recitation properly. Our online Quran academy in Canada serves every age category, be it children, teenagers, or adults. Our online Quran classes are available all across Canada, such as Toronto, Vancouver, Calgary, Montreal, Ottawa, Edmonton, Mississauga, and Brampton, among others.
            </p>

            {/* Stats list */}
            <div className="ny-hero__stats">
              <div className="ny-stat-item">
                <span className="ny-stat-value">{experience} +</span>
                <span className="ny-stat-label">Years Of Experience</span>
              </div>
              <div className="ny-stat-item">
                <span className="ny-stat-value">{students.toLocaleString()} +</span>
                <span className="ny-stat-label">Students Studied</span>
              </div>
              <div className="ny-stat-item">
                <span className="ny-stat-value">{tutors}+</span>
                <span className="ny-stat-label">Expert Tutors</span>
              </div>
            </div>
          </div>

          {/* Right Column: Sign Up Form Card */}
          <div className="ny-hero__form-card">
            <div className="ny-form-header">
              <h2 className="ny-form-title">GET STARTED</h2>
              <p className="ny-form-subtitle">SIGN UP FOR FREE</p>
            </div>

            <form onSubmit={handleSubmit} className="ny-form">
              <div className="ny-form-row">
                <div className="ny-form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="ny-form-input"
                  />
                </div>
                <div className="ny-form-group">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="ny-form-input"
                  />
                </div>
              </div>

              <div className="ny-form-group">
                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  className="ny-form-textarea"
                />
              </div>

              {/* reCAPTCHA integration */}
              <div className="ny-recaptcha-container">
                <div
                  className="g-recaptcha"
                  data-sitekey={
                    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                      ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI' // Public Google test key for localhost development
                      : '6LdiHz4tAAAAAONg4UJVDRkG1K3enavhCUQbnmLx' // Production key for alquranlesson.com
                  }
                ></div>
              </div>

              <button type="submit" className="ny-form-submit-btn">
                Send
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Intro Info Section */}
      <section className="ny-intro-section">
        <div className="ny-intro-container">
          {/* Left Column: Text & CTA */}
          <div className="ny-intro-content">
            <h2 className="ny-intro-title">
              Reliable and Trusted
              <span className="ny-intro-title--green"> Online Quran Classes in Canada</span>
            </h2>
            <p className="ny-intro-desc">
              At the online Quran academy Canada, we provide structured and <a href="/" className="ny-intro-link">live online Quran classes</a> that help students read the Quran clearly and regularly with confidence. Our platform provides students with highly qualified tutors to teach them to read the Quran with proper Tajweed.
            </p>
            <p className="ny-intro-desc">
              We pride ourselves on being one of the most trusted resources for families wanting to take online Quran lessons for their kids across the country, providing flexible class schedules for all students to achieve their educational goals. From beginning to adult students wanting to learn Tajweed, our commitment to providing a high-quality education for all of our students is second to none; that’s why we are always one of the top academies when looking for online Quran classes near me.
            </p>
            <a href="/free-trial/" className="ny-intro-btn">
              START FREE TRIAL CLASSES TODAY
            </a>
          </div>

          {/* Right Column: Image */}
          <div className="ny-intro-image-col">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-canada/intro-img.jpg`}
              alt="Reliable and Trusted Online Quran Classes in Canada​"
              className="ny-intro-img"
              style={{
                WebkitMaskImage: `url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-canada/masking.webp')`,
                WebkitMaskSize: 'contain',
                WebkitMaskPosition: 'center',
                WebkitMaskRepeat: 'no-repeat',
                maskImage: `url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-canada/masking.webp')`,
                maskSize: 'contain',
                maskPosition: 'center',
                maskRepeat: 'no-repeat'
              }}
            />
          </div>
        </div>
      </section >

      {/* Courses Offered Section */}
      < section className="ny-courses-section" >
        <div className="ny-courses-container">
          <div className="ny-courses-header">
            <h2 className="ny-courses-badge">Courses Offered</h2>
            <h2 className="ny-courses-title">Online Quran Courses Available In Canada</h2>
            <div className="ny-courses-divider">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/pshape.png`}
                alt="Divider"
                className="ny-courses-divider-img"
              />
            </div>
          </div>

          <div className="ny-courses-grid">
            {/* Left Column: Image */}
            <div className="ny-courses-image-col">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-canada/course-img.webp`}
                alt="Quran courses for kids and adults in Canada"
                className="ny-courses-img"
              />
            </div>

            {/* Right Column: Course list */}
            <div className="ny-courses-list">
              {/* Course 1 */}
              <a href="/quran-reading-classes-online-for-kids-and-adults/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg aria-hidden="true" className="e-font-icon-svg e-fas-book-open" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg"><path d="M542.22 32.05c-54.8 3.11-163.72 14.43-230.96 55.59-4.64 2.84-7.27 7.89-7.27 13.17v363.87c0 11.55 12.63 18.85 23.28 13.49 69.18-34.82 169.23-44.32 218.7-46.92 16.89-.89 30.02-14.43 30.02-30.66V62.75c.01-17.71-15.35-31.74-33.77-30.7zM264.73 87.64C197.5 46.48 88.58 35.17 33.78 32.05 15.36 31.01 0 45.04 0 62.75V400.6c0 16.24 13.13 29.78 30.02 30.66 49.49 2.6 149.59 12.11 218.77 46.95 10.62 5.35 23.21-1.94 23.21-13.46V100.63c0-5.29-2.62-10.14-7.27-12.99z"></path></svg>
                </span>
                <span className="ny-course-text">Quran Reading Classes Online</span>
              </a>

              {/* Course 2 */}
              <a href="/online-tajweed-classes-tajweed-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg aria-hidden="true" className="e-font-icon-svg e-fas-praying-hands" viewBox="0 0 640 512" xmlns="http://www.w3.org/2000/svg"><path d="M272 191.91c-17.6 0-32 14.4-32 32v80c0 8.84-7.16 16-16 16s-16-7.16-16-16v-76.55c0-17.39 4.72-34.47 13.69-49.39l77.75-129.59c9.09-15.16 4.19-34.81-10.97-43.91-14.45-8.67-32.72-4.3-42.3 9.21-.2.23-.62.21-.79.48l-117.26 175.9C117.56 205.9 112 224.31 112 243.29v80.23l-90.12 30.04A31.974 31.974 0 0 0 0 383.91v96c0 10.82 8.52 32 32 32 2.69 0 5.41-.34 8.06-1.03l179.19-46.62C269.16 449.99 304 403.8 304 351.91v-128c0-17.6-14.4-32-32-32zm346.12 161.73L528 323.6v-80.23c0-18.98-5.56-37.39-16.12-53.23L394.62 14.25c-.18-.27-.59-.24-.79-.48-9.58-13.51-27.85-17.88-42.3-9.21-15.16 9.09-20.06 28.75-10.97 43.91l77.75 129.59c8.97 14.92 13.69 32 13.69 49.39V304c0 8.84-7.16 16-16 16s-16-7.16-16-16v-80c0-17.6-14.4-32-32-32s-32 14.4-32 32v128c0 51.89 34.84 98.08 84.75 112.34l179.19 46.62c2.66.69 5.38 1.03 8.06 1.03 23.48 0 32-21.18 32-32v-96c0-13.77-8.81-25.99-21.88-30.35z"></path></svg>
                </span>
                <span className="ny-course-text">Quran Tajweed Classes</span>
              </a>

              {/* Course 3 */}
              <a href="/quran-memorization-classes-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg aria-hidden="true" className="e-font-icon-svg e-fas-mosque" viewBox="0 0 640 512" xmlns="http://www.w3.org/2000/svg"><path d="M0 480c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V160H0v320zm579.16-192c17.86-17.39 28.84-37.34 28.84-58.91 0-52.86-41.79-93.79-87.92-122.9-41.94-26.47-80.63-57.77-111.96-96.22L400 0l-8.12 9.97c-31.33 38.45-70.01 69.76-111.96 96.22C233.79 135.3 192 176.23 192 229.09c0 21.57 10.98 41.52 28.84 58.91h358.32zM608 320H192c-17.67 0-32 14.33-32 32v128c0 17.67 14.33 32 32 32h32v-64c0-17.67 14.33-32 32-32s32 14.33 32 32v64h64v-72c0-48 48-72 48-72s48 24 48 72v72h64v-64c0-17.67 14.33-32 32-32s32 14.33 32 32v64h32c17.67 0 32-14.33 32-32V352c0-17.67-14.33-32-32-32zM64 0S0 32 0 96v32h128V96c0-64-64-96-64-96z"></path></svg>
                </span>
                <span className="ny-course-text">Quran Memorization Classes</span>
              </a>

              {/* Course 4 */}
              <a href="/online-quran-translation-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg aria-hidden="true" className="e-font-icon-svg e-fas-kaaba" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg"><path d="M554.12 83.51L318.36 4.93a95.962 95.962 0 0 0-60.71 0L21.88 83.51A32.006 32.006 0 0 0 0 113.87v49.01l265.02-79.51c15.03-4.5 30.92-4.5 45.98 0l265 79.51v-49.01c0-13.77-8.81-26-21.88-30.36zm-279.9 30.52L0 196.3v228.38c0 15 10.42 27.98 25.06 31.24l242.12 53.8a95.937 95.937 0 0 0 41.65 0l242.12-53.8c14.64-3.25 25.06-16.24 25.06-31.24V196.29l-274.2-82.26c-9.04-2.72-18.59-2.72-27.59 0zM128 230.11c0 3.61-2.41 6.77-5.89 7.72l-80 21.82C37.02 261.03 32 257.2 32 251.93v-16.58c0-3.61 2.41-6.77 5.89-7.72l80-21.82c5.09-1.39 10.11 2.44 10.11 7.72v16.58zm144-39.28c0 3.61-2.41 6.77-5.89 7.72l-96 26.18c-5.09 1.39-10.11-2.44-10.11-7.72v-16.58c0-3.61 2.41-6.77 5.89-7.72l96-26.18c5.09-1.39 10.11 2.44 10.11 7.72v16.58zm176 22.7c0-5.28 5.02-9.11 10.11-7.72l80 21.82c3.48.95 5.89 4.11 5.89 7.72v16.58c0 5.28-5.02 9.11-10.11 7.72l-80-21.82a7.997 7.997 0 0 1-5.89-7.72v-16.58zm-144-39.27c0-5.28 5.02-9.11 10.11-7.72l96 26.18c3.48.95 5.89 4.11 5.89 7.72v16.58c0 5.28-5.02 9.11-10.11 7.72l-96-26.18a7.997 7.997 0 0 1-5.89-7.72v-16.58z"></path></svg>
                </span>
                <span className="ny-course-text">Quran Translation Classes Online</span>
              </a>

              {/* Course 5 */}
              <a href="/noorani-qaida-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg aria-hidden="true" className="e-font-icon-svg e-fas-kaaba" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg"><path d="M554.12 83.51L318.36 4.93a95.962 95.962 0 0 0-60.71 0L21.88 83.51A32.006 32.006 0 0 0 0 113.87v49.01l265.02-79.51c15.03-4.5 30.92-4.5 45.98 0l265 79.51v-49.01c0-13.77-8.81-26-21.88-30.36zm-279.9 30.52L0 196.3v228.38c0 15 10.42 27.98 25.06 31.24l242.12 53.8a95.937 95.937 0 0 0 41.65 0l242.12-53.8c14.64-3.25 25.06-16.24 25.06-31.24V196.29l-274.2-82.26c-9.04-2.72-18.59-2.72-27.59 0zM128 230.11c0 3.61-2.41 6.77-5.89 7.72l-80 21.82C37.02 261.03 32 257.2 32 251.93v-16.58c0-3.61 2.41-6.77 5.89-7.72l80-21.82c5.09-1.39 10.11 2.44 10.11 7.72v16.58zm144-39.28c0 3.61-2.41 6.77-5.89 7.72l-96 26.18c-5.09 1.39-10.11-2.44-10.11-7.72v-16.58c0-3.61 2.41-6.77 5.89-7.72l96-26.18c5.09-1.39 10.11 2.44 10.11 7.72v16.58zm176 22.7c0-5.28 5.02-9.11 10.11-7.72l80 21.82c3.48.95 5.89 4.11 5.89 7.72v16.58c0 5.28-5.02 9.11-10.11 7.72l-80-21.82a7.997 7.997 0 0 1-5.89-7.72v-16.58zm-144-39.27c0-5.28 5.02-9.11 10.11-7.72l96 26.18c3.48.95 5.89 4.11 5.89 7.72v16.58c0 5.28-5.02 9.11-10.11 7.72l-96-26.18a7.997 7.997 0 0 1-5.89-7.72v-16.58z"></path></svg>
                </span>
                <span className="ny-course-text">Noorani Qaida Course</span>
              </a>
            </div>
          </div>
        </div>
      </section >

      {/* Why Choose Section */}
      < section className="ny-why-section" >
        <div className="ny-why-container">
          {/* Left Column: Text & CTA */}
          <div className="ny-why-content">
            <h2 className="ny-why-title">
              Flexible Online Quran Education for <span className="ny-why-title--green">Canadian Students</span>
            </h2>
            <p className="ny-why-desc">
              We design our curriculum to meet the learning requirements of students in Canada, while also being flexible for an individual’s availability. Qualified teachers provide instruction, enabling the student to learn at home. We have a vast curriculum that consists of Quran & Tajweed, as well as Hifz (memorization of the Quran), for young children, teenagers, and adults. If you are looking for <a style={{ color: "#006100" }} href="/quran-memorization-classes-course/">Quran memorization classes</a> or an online platform that offers extensive Quranic learning, you can expect to have a great learning experience at Al Quran Lesson.

            </p>

            <a href="/free-trial/" className="ny-why-btn">
              GET FREE TRIAL CLASSES
            </a>
          </div>

          {/* Right Column: Visual Stats Cards */}
          <div className="ny-why-stats-cards">
            {/* Card 1 */}
            <div className="ny-why-card">
              <div className="ny-why-card-left">
                <span className="ny-why-card-icon-box">
                  <svg className="ny-why-card-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .9-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" />
                  </svg>
                </span>
                <span className="ny-why-card-label">Years Of Experience</span>
              </div>
              <div className="ny-why-card-right-container">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/right-image.webp`}
                  alt="Stats visual background"
                  className="ny-why-card-right-img"
                />
                <span className="ny-why-card-value">16+</span>
              </div>
            </div>

            {/* Card 2 */}
            <div className="ny-why-card">
              <div className="ny-why-card-left">
                <span className="ny-why-card-icon-box">
                  <svg className="ny-why-card-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                  </svg>
                </span>
                <span className="ny-why-card-label">Students Studied</span>
              </div>
              <div className="ny-why-card-right-container">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/right-image.webp`}
                  alt="Stats visual background"
                  className="ny-why-card-right-img"
                />
                <span className="ny-why-card-value">5000+</span>
              </div>
            </div>

            {/* Card 3 */}
            <div className="ny-why-card">
              <div className="ny-why-card-left">
                <span className="ny-why-card-icon-box">
                  <svg className="ny-why-card-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zm0 18c-4.41 0-8-3.59-8-8c0-.62.08-1.21.21-1.79L9 15v-2.24l-3-2.14V7.57l6-3.43l6 3.43v3.05l-3 2.14V15l4.79-3.79c.13.58.21 1.17.21 1.79c0 4.41-3.59 8-8 8z" />
                  </svg>
                </span>
                <span className="ny-why-card-label">Expert Tutors</span>
              </div>
              <div className="ny-why-card-right-container">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/right-image.webp`}
                  alt="Stats visual background"
                  className="ny-why-card-right-img"
                />
                <span className="ny-why-card-value">100+</span>
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* Features Cards Parallax Section */}
      < section
        className="ny-features-cards-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/thumb_16734896_cover_header.webp')`
        }}
      >
        <div className="ny-features-cards-container">
          <h2 className="ny-features-cards-title">Why Choose Al Quran Lesson in Canada?</h2>
          <div className="ny-features-cards-divider">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/pshape.png`}
              alt="Divider"
              className="ny-features-cards-divider-img"
            />
          </div>

          <div className="ny-features-cards-grid">
            {/* Card 1 */}
            <div className="ny-feature-card-item">
              <div className="ny-feature-card-icon-circle">
                <svg className="ny-feature-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
                </svg>
              </div>
              <h3 className="ny-feature-card-title">Since 2013</h3>
              <p className="ny-feature-card-desc">
                We have been imparting our knowledge to students from all across the USA, UK, and Canada since 2013. We have Quran tutors who are experts in teaching not only children but also adults. All of our tutors are proficient in the field of Quranic recitation and Tajweed.
              </p>
            </div>

            {/* Card 2 */}
            <div className="ny-feature-card-item">
              <div className="ny-feature-card-icon-circle">
                <svg className="ny-feature-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
                </svg>
              </div>
              <h3 className="ny-feature-card-title">Flexible Scheduling</h3>
              <p className="ny-feature-card-desc">

                Our online Quran classes for kids in Canada are available across different time zones to suit your routine. This flexibility allows students to stay consistent without disturbing school, work, or daily commitments. We can customize our timings to meet your daily schedule. Check our <a href="/quran-classes-fee/" className="ny-feature-card-link">fee packages</a> to find a plan that fits your budget.
              </p>
            </div>

            {/* Card 3 */}
            <div className="ny-feature-card-item">
              <div className="ny-feature-card-icon-circle">
                <svg className="ny-feature-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                </svg>
              </div>
              <h3 className="ny-feature-card-title">3-Day Free Trial</h3>
              <p className="ny-feature-card-desc">
                Experience our teaching method with three free sessions. Meet your tutor, understand the process, and evaluate the quality before making any commitment. Students can easily understand the teaching style of the teacher in this 3-day trial.
              </p>
            </div>
          </div>
        </div>
      </section >

      {/* FAQs Section */}
      < section className="ny-faq-section" >
        <div className="ny-faq-container">
          <h2 className="ny-faq-heading">
            FREQUENTLY ASKED QUESTIONS <span className="ny-faq-heading--green">(FAQS)</span>
          </h2>

          <div className="ny-faq-grid">
            {/* Left Column FAQs */}
            <div className="ny-faq-col">
              {/* FAQ 1 */}
              <div className={`ny-faq-item ${activeFaqs[0] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(0)}>
                  <span>Do you teach students across Canada?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Yes, our Quran Academy Canada teaches online Quran classes all over Canada while maintaining quality education consistently.
                  </div>
                </div>
              </div>

              {/* FAQ 2 */}
              <div className={`ny-faq-item ${activeFaqs[1] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(1)}>
                  <span>What age of students can enroll in your classes?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Our online Quran classes in Canada are available for people of all age groups. Kids, teenagers, and adults can all join the classes.
                  </div>
                </div>
              </div>

              {/* FAQ 3 */}
              <div className={`ny-faq-item ${activeFaqs[2] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(2)}>
                  <span>How can the classes be manageable with a busy schedule?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    We offer very flexible time schedules. We also provide Quran teachings on weekends to fit your daily schedule.
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column FAQs */}
            <div className="ny-faq-col">
              {/* FAQ 4 */}
              <div className={`ny-faq-item ${activeFaqs[3] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(3)}>
                  <span>Are lessons done alone or in groups?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    We provide one-on-one attention in order to provide personalized service and improve progress through education.
                  </div>
                </div>
              </div>

              {/* FAQ 5 */}
              <div className={`ny-faq-item ${activeFaqs[4] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(4)}>
                  <span>What do I need to take online classes for the Quran?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    The only thing you need is a device, an internet connection, and a quiet place to learn. All of the learning resources will be provided by us.
                  </div>
                </div>
              </div>

              {/* FAQ 6 */}
              <div className={`ny-faq-item ${activeFaqs[5] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(5)}>
                  <span>Can I enroll several members of my family in these classes?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Yes. Multiple members from the same family can easily join us for various learning needs. Each family member can schedule times to attend classes at their convenience.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* Best Platform Section */}
      < section
        className="ny-platform-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/thumb_16734896_cover_header.webp')`
        }}
      >
        <div className="ny-platform-container">
          <h2 className="ny-platform-heading">
            Why Are Our Online Quran Classes a Top Choice for Canadian Families?
          </h2>
          <div className="ny-platform-divider">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/pshape.png`}
              alt="Divider"
              className="ny-platform-divider-img"
            />
          </div>

          <div className="ny-platform-grid">
            {/* Card 1: Focus */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 16h-2v-2h2v2zm0-4h-2V7h2v7z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Male/Female Tutors</h3>
              <p className="ny-platform-card-desc">
                We offer classes by both male and female Quran tutors to match your requirements and preferences. Parents are allowed to choose teachers who fulfill their regulations and values. It gives a more personalized and comfortable learning experience for every student.
              </p>
            </div>

            {/* Card 2: Confidence */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Individual Learning</h3>
              <p className="ny-platform-card-desc">
                Our personalized lessons are designed to target the exact needs of the students. This custom approach results in quick and efficient student development. Students are able to make their base strong in Quranic and Tajweed knowledge at a pace that is comfortable for them.
              </p>
            </div>

            {/* Card 3: Flexibility */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Safe Environment</h3>
              <p className="ny-platform-card-desc">
                All students can learn in a secure and respectful online learning environment. Our tutors have passed extensive background checks, and classes are held on secure digital platforms, ensuring the privacy and comfort of students in addition to an uninterrupted learning experience.
              </p>
            </div>

            {/* Card 4: Expertise */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Progress Tracking</h3>
              <p className="ny-platform-card-desc">
                We assess each student’s progress through periodical evaluations. This ensures that we provide our students with progress reports, thus encouraging continuous growth and better learning results in the long run.
              </p>
            </div>
          </div>
        </div>
      </section >

      {/* Enroll Today Section */}
      < section className="ny-enroll-section" >
        <div className="ny-enroll-container">
          {/* Left Column: Offset Image & Overlap Badge */}
          <div className="ny-enroll-image-col">
            <div className="ny-enroll-image-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/Al-Quran-Website-Image-01.webp`}
                alt="Learn Quran with Tajweed online in Canada"
                className="ny-enroll-img"
              />
            </div>
          </div>

          {/* Right Column: Text & CTA */}
          <div className="ny-enroll-content-col">
            <h2 className="ny-enroll-title">
              <span className="ny-enroll-title--green">Learn Quran Online In Canada</span> With Tajweed
            </h2>
            <p className="ny-enroll-desc">
              Families in Canada rely on us for high-quality and consistent Quran education. We offer a safe and supportive environment for learners with qualified teachers who guide them through structured and reliable online Quran classes for kids in Canada.
            </p>
            <p className="ny-enroll-desc">
              We provide <a style={{ color: "#006100", fontWeight: "bold" }} href="/online-tajweed-classes-tajweed-course/">online Quran reading with Tajweed</a> from beginner to advanced levels. Our goal is to provide a safe, effective, and reliable place for Quran recitation with Tajweed in Canada. Explore our <a style={{ color: "#006100", fontWeight: "bold" }} href="/quran-reading-classes-online-for-kids-and-adults/">Quran reading classes online</a> and <a style={{ color: "#006100", fontWeight: "bold" }} href="/quran-translation-classes/">Quran translation classes</a> as well.
            </p>
            <a href="/free-trial/" className="ny-enroll-btn">
              GET FREE TRIAL CLASSES
            </a>
          </div>
        </div>
      </section >

      {/* CTA Banner Section */}
      < section
        className="ny-cta-banner-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/green-bg.webp')`
        }}
      >
        <div className="ny-cta-banner-container">
          <h2 className="ny-cta-banner-text">
            Flexible Quran Classes With Tajweed In Canada. Get a Free Trial!
          </h2>
          <a href="/free-trial/" className="ny-cta-banner-btn">
            Start Learning
          </a>
        </div>
      </section >

      {/* Testimonials Slider Section */}
      < section className="ny-testimonials-section" >
        <div className="ny-testimonials-container">
          <h2 className="ny-testimonials-heading">
            What Parents and Students in Canada Think About Us

          </h2>

          {/* Gold Stars */}
          <div className="ny-testimonials-stars">
            {"★★★★★".split("").map((star, i) => (
              <span key={i} className="ny-star-icon">{star}</span>
            ))}
          </div>

          {/* Slider Container */}
          <div className="ny-testimonials-slider-box">
            {/* Left Control */}
            <button className="ny-slider-control prev" onClick={prevSlide} aria-label="Previous slide">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            {/* Slider Content Wrapper */}
            <div className="ny-slider-content-wrapper">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className={`ny-testimonial-slide ${idx === currentSlide ? 'active' : ''}`}
                >
                  <p className="ny-testimonial-text">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <span className="ny-testimonial-author">
                    {t.author}
                  </span>
                </div>
              ))}
            </div>

            {/* Right Control */}
            <button className="ny-slider-control next" onClick={nextSlide} aria-label="Next slide">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* Navigation Dots */}
          <div className="ny-testimonials-dots">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`ny-dot-btn ${idx === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section >

      {/* Custom Contact Info & Form Section */}
      < section className="ny-contact-section" >
        <div className="ny-contact-container">
          {/* Left Column: Contact Info */}
          <div className="ny-contact-info-col">
            <h4 className="ny-contact-info-title">CONTACT INFO</h4>
            <ul className="ny-contact-info-list">
              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </span>
                <a href="https://maps.google.com/?q=Mississauga,+Ontario,+L5K,+1W2" target="_blank" rel="noopener noreferrer" className="ny-contact-text">Mississauga, Ontario, L5K, 1W2</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
                <a href="tel:+16477385707" className="ny-contact-text">+1 647 738 5707 (Canada)</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
                <a href="tel:+17164577657" className="ny-contact-text">+1 716 457 7657 (USA)</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
                <a href="tel:+447441394281" className="ny-contact-text">+44 7441 394281 (UK)</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </span>
                <a href="mailto:info@alquranlesson.com" className="ny-contact-text">info@alquranlesson.com</a>
              </li>
            </ul>
          </div>

          {/* Right Column: Connect Form */}
          <div className="ny-contact-form-col">
            <h4 className="ny-contact-form-title">CONNECT WITH US TODAY</h4>
            <form
              onSubmit={handleContactSubmit}
              className="ny-contact-form"
              style={{
                backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.93), rgba(255, 255, 255, 0.93)), url('${process.env.PUBLIC_URL}/assets/images/green-bg.png')`,
              }}
            >
              {/* Row 1: First Name | Last Name | Phone No */}
              <div className="ny-contact-form-row three-cols">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={contactData.firstName}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-input"
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={contactData.lastName}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-input"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone No"
                  value={contactData.phone}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-input"
                />
              </div>

              {/* Row 2: Course & Country in one row */}
              <div className="ny-contact-form-row two-cols">
                <select
                  name="course"
                  value={contactData.course}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-select"
                >
                  <option value="">Course</option>
                  <option value="Noorani Qaida">Noorani Qaida</option>
                  <option value="Quran with Tajweed">Quran with Tajweed</option>
                  <option value="Quran Memorization">Quran Memorization</option>
                  <option value="Quran Translation">Quran Translation</option>
                  <option value="Arabic Grammar">Arabic Grammar</option>
                </select>
                <select
                  name="country"
                  value={contactData.country}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-select"
                >
                  <option value="">Country</option>
                  <option value="USA">USA</option>
                  <option value="Canada">Canada</option>
                  <option value="UK">UK</option>
                  <option value="Australia">Australia</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Row 4: Message */}
              <div className="ny-contact-field-full">
                <textarea
                  name="message"
                  placeholder="Message"
                  value={contactData.message}
                  onChange={handleContactChange}
                  className="ny-contact-textarea"
                />
              </div>

              {/* Checkbox Consent */}
              <div className="ny-contact-consent">
                <label className="ny-contact-consent-label">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={contactData.consent}
                    onChange={handleContactChange}
                    required
                    className="ny-contact-checkbox"
                  />
                  <span>
                    By submitting this form, you consent to receiving text messages and emails from Al Quran Lesson about your inquiry regarding Quran lessons. A representative will reach out to discuss your needs and provide further details. The frequency of messages may vary. You can unsubscribe at any time by replying 'STOP' to text messages or clicking the unsubscribe link in emails. Standard message and data rates may apply.
                  </span>
                </label>
              </div>

              <div className="ny-contact-recaptcha-wrapper">
                <div
                  className="g-recaptcha"
                  data-sitekey={
                    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                      ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
                      : '6LdiHz4tAAAAAONg4UJVDRkG1K3enavhCUQbnmLx'
                  }
                ></div>
              </div>

              <button type="submit" className="ny-contact-submit-btn">
                Submit
              </button>
            </form>
          </div>
        </div>
      </section >
    </div >
  );
};

export default OnlineQuranClassesCanada;

